import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import PropTypes from "prop-types";
import { projects2025Data } from "./projects2025Data";
import { ProjectCarousel } from "./ProjectDetailPage";
import Contacts from "./Contacts";

const ContentSectionRenderer = ({ section, index }) => {
  switch (section.type) {
    case "heading":
      const Tag = `h${section.level || 2}`;
      return (
        <Tag
          key={index}
          className="text-xl md:text-2xl font-semibold text-gray-800 mb-4 mt-6 first:mt-0"
        >
          {section.text}
        </Tag>
      );

    case "moduleList":
      return (
        <div key={index} className="mb-6 space-y-5">
          {section.modules?.map((group, groupIndex) => (
            <div key={groupIndex}>
              {group.title && (
                <h3 className="text-lg font-semibold text-blue-800 mb-2">
                  {group.title}
                </h3>
              )}
              {group.items?.length > 0 && (
                <ul className="list-disc list-outside space-y-1.5 text-gray-700 pl-5">
                  {group.items.map((item, itemIndex) => (
                    <li key={itemIndex}>{item}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      );

    case "paragraph":
      return (
        <p key={index} className="text-gray-700 mb-4">
          {section.text}
        </p>
      );

    case "achievement":
      return (
        <div
          key={index}
          className="mb-6 bg-blue-50 p-4 rounded-lg border border-blue-200 inline-block"
        >
          {section.title && (
            <h3 className="text-lg font-semibold text-blue-800 mb-2">
              {section.title}
            </h3>
          )}
          {section.value && (
            <p className="text-md text-gray-800 font-medium">{section.value}</p>
          )}
        </div>
      );

    case "gallery":
      return (
        <ProjectCarousel
          key={index}
          title={section.title}
          images={section.images}
        />
      );

    default:
      return null;
  }
};

ContentSectionRenderer.propTypes = {
  section: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
};

const Project2025DetailPage = () => {
  const { projectId } = useParams();
  const project = projects2025Data.find((p) => p.id.toString() === projectId);

  if (!project) {
    return <Navigate to="/projects" replace />;
  }

  return (
    <div>
      <div className="container mx-auto px-4 py-10 md:py-16 max-w-4xl font-montserrat">
        <Link
          to="/projects"
          className="text-blue-600 hover:underline mb-6 inline-block text-sm"
        >
          ← Ко всем курсам
        </Link>

        <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
          {project.title}
        </h1>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 mb-6 text-sm text-gray-600 border-b pb-4">
          {project.city && (
            <p>
              <strong className="text-gray-700 font-medium">Место проведения:</strong>{" "}
              {project.city}
            </p>
          )}
          {project.date && (
            <p>
              <strong className="text-gray-700 font-medium">Период обучения:</strong>{" "}
              {project.date}
            </p>
          )}
          <p>
            <strong className="text-gray-700 font-medium">Категория слушателей:</strong>{" "}
            {project.participantCategory}
          </p>
          <p>
            <strong className="text-gray-700 font-medium">
              Общее кол-во слушателей:
            </strong>{" "}
            {project.totalParticipants}
          </p>
          <p className="sm:col-span-2 mt-2">
            <strong className="text-gray-700 font-medium">Цель:</strong>{" "}
            {project.aim}
          </p>
        </div>

        {project.contentSections &&
          Array.isArray(project.contentSections) &&
          project.contentSections.length > 0 && (
            <div className="mt-8">
              {project.contentSections.map((section, index) => (
                <ContentSectionRenderer
                  key={index}
                  section={section}
                  index={index}
                />
              ))}
            </div>
          )}

        {(!project.contentSections || project.contentSections.length === 0) && (
          <p className="text-gray-500 mt-8 italic">
            Дополнительная информация по этому курсу не указана.
          </p>
        )}
      </div>
      <Contacts />
    </div>
  );
};

export default Project2025DetailPage;
